// App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useI18n } from './i18n';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import UnderDevelopment from './components/UnderDevelopment';

// Home Page Components (kept static for LCP)
import WelcomeCarousel from './components/WelcomeCarousel';
import ExploreSection from './components/ExploreSection';
import ProjectsNews from './components/ProjectsNews';
import LatestUpdates from './components/LatestUpdates';
import MeetTeam from './components/MeetTeam';

const Solutions = lazy(() => import('./pages/Solutions'));
const About = lazy(() => import('./pages/About'));
const Updates = lazy(() => import('./pages/Updates'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  const { t, lang } = useI18n();

  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  
  if (isMaintenanceMode) {
    return <UnderDevelopment />;
  }

  const RouteTracker = () => {
    const location = useLocation();
    useEffect(() => {
      let consent = null;
      try {
        const v = window.localStorage.getItem('berrik_cookie_consent');
        consent = v ? JSON.parse(v).value : null;
      } catch {}
      if (consent !== 'accepted') return;
      if (process.env.NODE_ENV !== 'production') return;
      const payload = { type: 'pageview', path: location.pathname, title: document.title, referrer: document.referrer || '' };
      if (window.gtag) {
        window.gtag('event', 'page_view', { page_path: payload.path, page_title: payload.title });
      }
    }, [location.pathname]);
    return null;
  };

  return (
    <HelmetProvider>
      <Helmet>
        <html lang={lang} />
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <meta name="keywords" content="berrik, berrik chile, berrik engineering, berrik ingenieria, micuenca, mi cuenca, hydrology, hydraulics, flood risk, inundation mapping, automated modeling, water resources, ingeniería hidráulica, hidrología, riesgo de inundación" />
        <link rel="canonical" href="https://berrik.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EngineeringService",
            "name": "Berrik",
            "alternateName": ["Berrik Chile", "Berrik Engineering", "Berrik Ingeniería"],
            "url": "https://berrik.com",
            "logo": "https://berrik.com/logo512.png",
            "sameAs": [
              "https://www.linkedin.com/company/berrik",
              "https://instagram.com/berrik_chile"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "CL"
            },
            "description": t('seo.homeDescription'),
            "areaServed": ["Chile", "Latin America", "International"]
          })}
        </script>
      </Helmet>
      <BrowserRouter>
        <div className="App">
          <ScrollToTop />
          <RouteTracker />
          <Header />
          <CookieConsent />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <WelcomeCarousel />
                    <ExploreSection />
                    <ProjectsNews />
                    <LatestUpdates />
                    <MeetTeam />
                  </>
                }
              />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Navigate to="/" replace />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
